import { DateTimeFormatter, LocalDateTime } from '@js-joda/core';
import { Equal } from '@rivethealth/core/equal';
import { KeyFn } from '@rivethealth/core/map';
import { ServiceTypeCode } from 'x12-standard/element/1365';
import { GetEstimation } from './api/estimation';
import { BenefitItemType, PlaceOfServiceCode } from './benefit';
import { AutoBenefitEngine } from './benefit/auto-select/rules-engine';
import { ChargemasterItem } from './chargemaster';
import { Cpt } from './cpt';
import { isoDate } from './date';
import {
  EligibilityBenefit,
  GetEligibility,
  MedicarePlanDetails,
  MedicarePlanTypeFilter,
  MedicareRollingCoverage,
} from './eligibility';
import { Id } from './id';
import { BusinessUnitType } from './location';
import { CollabNote } from './note';
import { Option } from './options';
import { ExplanationMessage } from './payment';
import {
  PaymentTransactionPostStatus,
  PaymentTransactionStatus,
  PaymentTransactionType,
} from './payment-transaction';
import { InPatientRate, OutPatientRate } from './schedule-form';
import { cptToOption } from './service-type/cpt-stc';
import {
  DateString,
  Filter,
  Result as SuperTableResponse,
} from './super-table';
import { getMessage, Language } from './translation';

export function readableTimePeriod(timePeriod: string) {
  switch (timePeriod) {
    case 'calendar_year':
      return 'Calendar Year';
    case 'twentyfour_hours':
      return '24 hours';
    case 'service_year':
      return 'Service Year';
  }
  return timePeriod.charAt(0).toUpperCase() + timePeriod.slice(1);
}

export type AllowedEstimateType = 'schedule' | 'claims';

export type AllowedEstimate = {
  allowed: string | null;
  allowedType: AllowedEstimateType | null;
  claimRate: string | null;
  scheduleRate: string | null;
  rateType: OutPatientRate['type'] | InPatientRate['type'] | null;
  logs: ExplanationMessage[];
  grossCharge: GrossCharge;
  apgCode?: string;
};

export type GrossCharge = {
  charge: number | null;
  logs: ExplanationMessage[];
};

export interface CreateAllowedEstimate {
  accountId: number;
  insuranceProductId: number | null;
  locationId: number | null;
  patientDob: string | null;
  placeOfService: PlaceOfServiceCode | null;
  procedures: QuantifiedRevCodeProcedure[];
  provider: { npiId: number } | null;
  scheduleId: number | null;
  serviceDate: string;
  drgCode: string | null;
  estimatedStay: number;
  treatmentType?: TreatmentType;
}

export interface PayerAllowedInput {
  scheduleId: number | null;
  insuranceProductId: number | null;
  ordinal: number;
}

export interface TreatmentAllowedInput {
  accountId: number;
  locationId: number | null;
  placeOfService: PlaceOfServiceCode | null;
  patientDob: string | null;
  provider: { npiId: number } | null;
  procedures: QuantifiedRevCodeProcedure[];
  serviceDate: string;
  payerInputs: PayerAllowedInput[];
  drgCode: string | null;
  estimatedStay: number;
  diagnosisCodes?: string[];
  eapgRequestConfirmed?: boolean;
  treatmentType?: TreatmentType;
}

export type DrgAllowed = {
  allowed: number | null;
  logs: ExplanationMessage[];
  rateType: OutPatientRate['type'] | InPatientRate['type'] | null;
};

export interface PayerAllowedResponse {
  ordinal: number;
  drgAllowed: DrgAllowed | null;
  procedureAlloweds: AllowedEstimate[] | null;
  error: string | null;
}

export interface TreatmentAllowedResponse {
  payerAlloweds: PayerAllowedResponse[];
}

export interface EstimateSummary {
  finalizedByUserId: number | null;
  hasManualPayment: boolean;
  hasTransaction: boolean;
  manualPaymentAmount: number | null;
  patient: {
    id: number;
    name: {
      first: string;
      last: string;
    };
  } | null;
  serviceDate: string;
}

export type BenefitNetwork = 'in' | 'out' | 'not_applicable' | 'unknown' | null;

export type BalanceBills = 'in' | 'out' | 'both' | null;

export interface EstimatePayment {
  totalAmount: number;
  manualAmount?: number;
  dateReceived?: string;
}
export interface EstimationFinancialSummary {
  id: number;
  estimate: Estimate;
  payment?: EstimatePayment;
  paymentRequest?: EstimatePaymentRequest;
}

export type EstimationTransaction = {
  transactionType: 'manual' | 'credit_card';
  amount: number;
  timestamp: string | null;
  externalType?: PaymentTransactionType | null;
  status?: PaymentTransactionStatus | null;
  postStatus?: PaymentTransactionPostStatus | null;
};

export interface EstimateTransactionsList {
  payment: EstimatePayment;
  transactions: EstimationTransaction[];
}

export interface EstimationListItem {
  id: number;
  estimate: Estimate;
  created: string | null;
  modified: string;
  patient: {
    id: number;
    name: {
      first: string;
      last: string;
    };
    insuranceProducts: string[];
    ehrRef: string;
  } | null;
  serviceLocationName: string;
  procedureCount: number;
  user: {
    id: number;
    name: string;
  } | null;
  providers: EstimationSearchProvider[] | null;
  schedule: {
    id: number;
    name: string;
  } | null;
  serviceDate: string;
  status: TableEstimationStatus;
  sentStatus: EstimationSentStatus[];
  mostRecentEvent: { event: string; timestamp: string } | null;
  paymentRequest?: EstimatePaymentRequest;
  payment?: EstimatePayment;
  appointmentType?: string;
  dueDate: string;
  assignedUsers: { id: number; name: string }[];
  syncStatuses: SyncStatus[];
  syncErrors: string[];
  hasDoc: boolean;
  accuracyInfo?: EstimationClaimMultiMatch;
  primaryEligibility: MedicarePlanDetails | null;
  dataSource: string | null;
  paymentCredentialId?: number | null;
  lastTransactionDate: string | null;
}

interface EstimationSearchProvider {
  npiId: number;
  name: {
    first: string;
    last: string;
  };
}

export type EstimationStatusFilter = TableEstimationStatus | 'sent';

export type EstimationFilter = {
  userId: Filter<number[]>;
  createDate: Filter<DateString[]>;
  serviceDate: EstimationDateFilter;
  customServiceDate: Filter<DateString[]>;
  procedureCount: Filter<number[]>;
  patientResponsibility: Filter<number[]>;
  allowedAmount: Filter<number[]>;
  requestedAmount: Filter<number[]>;
  collectedAmount: CollectedAmountFilter;
  customCollectedAmount: Filter<number[]>;
  serviceLocationName: Filter<string[]>;
  providerNPI: Filter<number[]>;
  dueDate: EstimationDateFilter;
  customDueDate: Filter<DateString[]>;
  appointmentTypes: Filter<string[]>;
  insuranceProductRefs: Filter<string[]>;
  status: Filter<EstimationStatusFilter[]>;
  sentStatus: Filter<EstimationSentStatus[]>;
  assignedUserIds: Filter<string[]>;
  medicarePlanType: Filter<MedicarePlanTypeFilter[]>;
  cptCodes: Filter<string[]>;
  businessUnitName: Filter<string[]>;
  dataSource: Filter<string[]>;
};

type EstimationDateFilter = '' | 'today' | 'custom';

type CollectedAmountFilter =
  | ''
  | 'lt-requested'
  | 'lt-responsibility'
  | 'custom';

export type EstimationSort =
  | 'default'
  | 'createDate'
  | 'serviceDate'
  | 'serviceLocationName'
  | 'id'
  | 'procedureCount'
  | 'allowedAmount'
  | 'patientResponsibility'
  | 'requestedAmount'
  | 'collectedAmount'
  | 'patient'
  | 'patientRef'
  | 'creatorName'
  | 'lastTransactionDate';

export type EstimationFacetCounts = {
  userId: object;
  providerNPI: object;
  appointmentTypes: object;
  serviceLocationName: object;
  insuranceProductRefs: object;
  assignedUserIds: object;
  medicarePlanType: object;
  cptCodes: object;
  businessUnitName: object;
  dataSource: object;
};

export type EstimationSearchResultCounts = {
  matched: number; // permitted + filters + search (no pagination)
  permitted: number; // permitted
} & EstimationFacetCounts;

export type EstimationSuperTableResponse =
  SuperTableResponse<EstimationListItem> & {
    counts?: EstimationSearchResultCounts;
  };

export type EstimationFilteredTotals = {
  numberOfEstimates: number;
  totalPatientResponsibility: number;
  amountCollected: number;
  amountOutstanding: number;
};

export interface Procedure {
  code: string;
  modifier: string;
}

export type QuantifiedProcedure = Procedure & {
  quantity: number;
};

export type QuantifiedRevCodeProcedure = {
  code: string;
  modifier: string;
  quantity: number;
  revenueCode: string | null;
  chargeAmountOverride: number | null;
  apgCode?: string | null;
};

export type EstimationNote = CollabNote;

export interface Estimate {
  allowed: number;
  patient: number;
}

export type EstimationId =
  | { type: 'account'; accountId: number }
  | { type: 'patient'; patientId: number };

export interface EstimationBenefit {
  copay: EstimationBenefit.Copay;
  coinsurance: EstimationBenefit.Coinsurance;
  deductible: EstimationBenefit.Deductible;
  oop: EstimationBenefit.Oop;
  limitation: EstimationBenefit.Limitation;
}

export namespace EstimationBenefit {
  export type AnyBenefit = Copay | Coinsurance | Deductible | Oop | Limitation;

  interface BaseEstimationBenefit {
    eligibilityBenefit: EligibilityBenefit | null;
    custom: boolean;
    autoSelectExplanation?: AutoBenefitEngine.BenefitExplanation;
  }

  export interface Copay extends BaseEstimationBenefit {
    amount: number | null;
  }

  export interface Coinsurance extends BaseEstimationBenefit {
    proportion: number | null;
  }

  export interface Deductible extends BaseEstimationBenefit {
    amount: number | null;
    total: number | null;
    cascadingBenefit: boolean;
    cumulativeAmount?: number;
  }

  export interface Oop extends BaseEstimationBenefit {
    amount: number | null;
    total: number | null;
    cascadingBenefit: boolean;
    cumulativeAmount?: number;
  }

  export interface Limitation extends BaseEstimationBenefit {}
}

export type CumulativeBenefits = {
  deductible: EstimationBenefit.Deductible;
  oop: EstimationBenefit.Oop;
};

export type CascadingBenefitsState = {
  cascadingDeductible: boolean;
  cascadingOOP: boolean;
};

export type NetworkStatus = 'in' | 'out';

export interface EstimationPayer {
  enabled: boolean;
  eligibilityId: number | null;
  eligibilityStatus: string;
  payerId: number | null;
  scheduleId: number | null;
  network: NetworkStatus;
  benefit: EstimationBenefit;
  tradingPartnerRef: string | null;
  /*
    isMedicarePartB is whether the eligibility from this payer has active Part B coverage.
    useMedicareABenefits is whether we should display/calculate using Medicare A benefits.
    medicarePartABenefits is the benefits we will display if we are using Part A benefits.
  */
  isMedicarePartB: boolean | null;
  useMedicareABenefits: boolean | null;
  medicarePartABenefits: SavedMedicarePartABenefits | null;

  networkExceptionApplied: boolean;
  tierExceptionApplied: boolean;
  benefitTypeExceptionApplied: boolean;

  /** Maximum number of PT/OT units payer will cover per encounter */
  ptOtMaxUnits: number | null;

  /** Deductible amount remaining after this payer+treatment */
  cumulativeDeductible: number | null;
  /** OOP amount remaining after this payer+treatment */
  cumulativeOop: number | null;
  /** The benefits we auto-selected on this payer+treatment */
  autoSelectedBenefits: EstimationBenefit | null;
}
export namespace EstimationPayer {
  export function empty(
    payerId: number,
    enabled: boolean,
    tradingPartnerRef: string | null,
  ): EstimationPayer {
    return {
      enabled,
      eligibilityId: null,
      payerId: payerId,
      scheduleId: null,
      network: 'in',
      benefit: {
        coinsurance: {
          eligibilityBenefit: null,
          proportion: null,
          custom: false,
        },
        deductible: {
          amount: null,
          eligibilityBenefit: null,
          total: null,
          custom: false,
          cascadingBenefit: true,
        },
        copay: {
          amount: null,
          eligibilityBenefit: null,
          custom: false,
        },
        oop: {
          amount: null,
          eligibilityBenefit: null,
          total: null,
          custom: false,
          cascadingBenefit: true,
        },
        limitation: {
          eligibilityBenefit: null,
          custom: false,
        },
      },
      isMedicarePartB: false,
      medicarePartABenefits: null,
      eligibilityStatus: 'missing',
      tradingPartnerRef,
      useMedicareABenefits: false,
      networkExceptionApplied: false,
      tierExceptionApplied: false,
      benefitTypeExceptionApplied: false,
      ptOtMaxUnits: null,
      autoSelectedBenefits: null,
      cumulativeDeductible: null,
      cumulativeOop: null,
    };
  }
}

export enum ProcedureCoverage {
  COPAY_ONLY = 'copay-only',
  FULLY_COVERED = 'fully-covered',
  NOT_COVERED = 'not-covered',
  STANDARD = 'standard',
}

export type EstimateLog =
  | EstimateLog.Allowed
  | EstimateLog.Coinsurance
  | EstimateLog.Deductible
  | EstimateLog.DeductibleMet
  | EstimateLog.Copay
  | EstimateLog.CopayDeductibleNotMet
  | EstimateLog.Coverage
  | EstimateLog.Oop
  | EstimateLog.Payer
  | EstimateLog.Procedure
  | EstimateLog.DRG
  | EstimateLog.Maximum
  | EstimateLog.WriteOff
  | EstimateLog.BalanceBill
  | EstimateLog.NumberOfXLimitation
  | EstimateLog.InvalidEligibility
  | EstimateLog.MedicareADeductible
  | EstimateLog.MedicareACoinsuranceDays
  | EstimateLog.MedicareACoinsuranceUsed
  | EstimateLog.MedicareAReserveDays
  | EstimateLog.MedicareAReserveUsed
  | EstimateLog.MedicareBCoinsuranceCap;

export namespace EstimateLog {
  export const ALLOWED = 'allowed';
  export const COINSURANCE = 'coinsurance';
  export const COPAY = 'copay';
  export const COPAYDEDUCTIBLENOTMET = 'copaydeductiblenotmet';
  export const COVERAGE = 'coverage';
  export const DEDUCTIBLE = 'deductible';
  export const DEDUCTIBLE_MET = 'deductiblemet';
  export const OOP = 'oop';
  export const PAYER = 'payer';
  export const PROCEDURE = 'procedure';
  export const DRG = 'drg';
  export const MAXIMUM = 'maximum';
  export const WRITEOFF = 'writeoff';
  export const BALANCEBILL = 'balancebill';
  export const NUMBEROFXLIMITATION = 'numberofxlimitation';
  export const INVALIDELIGIBILITY = 'invalideligibility';
  export const MEDICAREADEDUCTIBLE = 'medicareadeductible';
  export const MEDICAREACOINSURANCEDAYS = 'medicareacoinsurancedays';
  export const MEDICAREACOINSURANCEUSED = 'medicareacoinsuranceused';
  export const MEDICAREARESERVEDAYS = 'medicareareservedays';
  export const MEDICAREARESERVEUSED = 'medicareareserveused';
  export const MEDICAREBCOINSURANCECAP = 'medicarebcoinsurancecap';

  export interface Allowed {
    type: typeof ALLOWED;
    allowed: number | null;
    source: 'original' | 'patient_responsibility';
  }

  export interface Coverage {
    type: typeof COVERAGE;
    coverage: ProcedureCoverage;
  }

  export interface Deductible {
    type: typeof DEDUCTIBLE;
    before: number;
    after: number;
    payment: number;
  }

  export interface DeductibleMet {
    type: typeof DEDUCTIBLE_MET;
  }

  export interface Copay {
    type: typeof COPAY;
    before: number;
    after: number;
    payment: number;
  }

  export interface CopayDeductibleNotMet {
    type: typeof COPAYDEDUCTIBLENOTMET;
    deductibleRemaining: null | number;
    copay: number;
  }

  export interface Coinsurance {
    type: typeof COINSURANCE;
    amount: number;
    payment: number;
  }

  export interface Oop {
    type: typeof OOP;
    before: number;
    after: number;
  }

  export interface Payer {
    type: typeof PAYER;
    payment: number;
  }

  export interface Procedure {
    type: typeof PROCEDURE;
    code: string;
    modifier: string;
    quantity: number;
  }

  export interface DRG {
    type: typeof DRG;
    code: string;
  }

  export interface Maximum {
    type: typeof MAXIMUM;
    before: number;
    after: number;
    payment: number;
  }

  export interface WriteOff {
    type: typeof WRITEOFF;
    amount: number;
  }

  export interface BalanceBill {
    type: typeof BALANCEBILL;
    amount: number;
  }

  export interface NumberOfXLimitation {
    type: typeof NUMBEROFXLIMITATION;
    quantityQualifier: string;
    remaining: string;
    timePeriod: string;
    total: string;
  }

  export interface InvalidEligibility {
    type: typeof INVALIDELIGIBILITY;
    eligibilityStatus: string;
  }

  export interface MedicareADeductible {
    type: typeof MEDICAREADEDUCTIBLE;
    payment: number;
  }
  export interface MedicareACoinsuranceDays {
    type: typeof MEDICAREACOINSURANCEDAYS;
    used: number;
    remaining: number;
  }
  export interface MedicareACoinsuranceUsed {
    type: typeof MEDICAREACOINSURANCEUSED;
    payment: number;
  }
  export interface MedicareAReserveDays {
    type: typeof MEDICAREARESERVEDAYS;
    used: number;
    remaining: number;
  }
  export interface MedicareAReserveUsed {
    type: typeof MEDICAREARESERVEUSED;
    payment: number;
  }

  export interface MedicareBCoinsuranceCap {
    type: typeof MEDICAREBCOINSURANCECAP;
    cap: number;
    uncappedAmount: number;
  }
}

export interface EstimationProcedureEstimate {
  allowed: number | null;
  allowedType: AllowedEstimateType | null;
  log: ExplanationMessage[];
  grossCharge: GrossCharge;
}

export interface EstimationProcedure {
  code: string;
  modifier: string;
  quantity: number;
  description: string | null;
  coverages: ProcedureCoverage[];
  coverageOverrides: ProcedureCoverage[];
  allowedOverride: number | null;
  chargeAmountOverride: number | null;
  // Currently, only the first payer is estimated
  estimate: EstimationProcedureEstimate;
  scheduleRates: (number | null)[];
  revenueCode: string | null;
  apgCode: string | null;
}

export namespace EstimationProcedure {
  export function validate(value: EstimationProcedure) {
    const emptyCode = !value.code;
    const floatingCode = emptyCode && value.revenueCode;
    return { emptyCode, valid: !emptyCode || floatingCode };
  }
}

export type EstimationProcedures = EstimationProcedure[];

export interface EstimationProceduresValidation {
  valid: boolean;
}

export namespace EstimationProcedures {
  export function validate(
    value: EstimationProcedures,
  ): EstimationProceduresValidation {
    return {
      valid: value.every(
        procedure => EstimationProcedure.validate(procedure).valid,
      ),
    };
  }
}

export const PREVENTIVE_CARE_CODES: string[] = [
  '0001A',
  '0002A',
  '0011A',
  '0012A',
  '0031A',
  '00812',
  '00851',
  '00952',
  '0403T',
  '0500T',
  '11976',
  '11981',
  '11982',
  '11983',
  '44388',
  '44389',
  '44392',
  '44394',
  '45331',
  '45333',
  '45334',
  '45338',
  '45346',
  '45381',
  '45382',
  '45384',
  // '45385', removed per RVT-4362
  '45388',
  '57170',
  '58300',
  '58301',
  '58340',
  '58565',
  '58600',
  '58605',
  '58611',
  '58615',
  '58670',
  '58671',
  '59425',
  '59426',
  '71271',
  '74263',
  '74740',
  '76706',
  '76977',
  '77063',
  '77067',
  '77080',
  '77081',
  '80055',
  '80061',
  '80081',
  '81007',
  '81025',
  '81162',
  '81163',
  '81164',
  '81165',
  '81166',
  '81167',
  '81211',
  '81212',
  '81213',
  '81214',
  '81215',
  '81216',
  '81217',
  '81528',
  '82017',
  '82136',
  '82247',
  '82261',
  '82270',
  '82274',
  '82465',
  '82565',
  '82775',
  '82947',
  '82948',
  '82950',
  '82951',
  '82952',
  '83020',
  '83021',
  '83036',
  '83051',
  '83498',
  '83516',
  '83655',
  '83718',
  '83719',
  '83721',
  '83722',
  '84030',
  '84437',
  '84443',
  '84478',
  '84702',
  '84703',
  '85013',
  '85014',
  '85018',
  '85025',
  '85027',
  '85660',
  '86480',
  '86481',
  '86580',
  '86592',
  '86593',
  '86631',
  '86632',
  '86689',
  '86701',
  '86702',
  '86703',
  '86780',
  '86850',
  '86901',
  '87081',
  '87084',
  '87086',
  '87088',
  '87110',
  '87170',
  '87270',
  '87320',
  '87340',
  '87341',
  '87389',
  '87390',
  '87391',
  '87490',
  '87491',
  '87492',
  '87590',
  '87591',
  '87592',
  '87623',
  '87624',
  '87625',
  '87801',
  '87806',
  '87810',
  '87850',
  '88141',
  '88142',
  '88143',
  '88147',
  '88148',
  '88150',
  '88152',
  '88153',
  '88155',
  '88164',
  '88165',
  '88166',
  '88167',
  '88174',
  '88175',
  '88720',
  '90460',
  '90461',
  '90471',
  '90472',
  '90473',
  '90474',
  '90619',
  '90620',
  '90621',
  '90630',
  '90632',
  '90633',
  '90634',
  '90636',
  '90644',
  '90647',
  '90648',
  '90649',
  '90650',
  '90651',
  '90653',
  '90654',
  '90655',
  '90656',
  '90657',
  '90658',
  '90660',
  '90661',
  '90662',
  '90664',
  '90666',
  '90667',
  '90668',
  '90670',
  '90672',
  '90673',
  '90674',
  '90680',
  '90681',
  '90682',
  '90685',
  '90686',
  '90687',
  '90688',
  '90689',
  '90694',
  '90696',
  '90697',
  '90698',
  '90700',
  '90702',
  '90707',
  '90710',
  '90713',
  '90714',
  '90715',
  '90716',
  '90723',
  '90732',
  '90733',
  '90734',
  '90736',
  '90739',
  '90740',
  '90743',
  '90744',
  '90746',
  '90747',
  '90748',
  '90750',
  '90756',
  '92551',
  '92552',
  '92553',
  '92558',
  '92586',
  '92587',
  '92588',
  '92650',
  '92651',
  '93784',
  '93786',
  '93788',
  '93790',
  '96040',
  '96110',
  '96127',
  '96150',
  '96151',
  '96152',
  '96153',
  '96161',
  '96372',
  '98960',
  '98961',
  '98962',
  '99078',
  '99173',
  '99174',
  '99177',
  '99188',
  '99341',
  '99342',
  '99343',
  '99344',
  '99345',
  '99347',
  '99348',
  '99349',
  '99350',
  '99381',
  '99382',
  '99383',
  '99384',
  '99385',
  '99386',
  '99387',
  '99391',
  '99392',
  '99393',
  '99394',
  '99395',
  '99396',
  '99397',
  '99401',
  '99402',
  '99403',
  '99404',
  '99406',
  '99407',
  '99408',
  '99409',
  '99411',
  '99412',
  '99417',
  '99461',
  'A4261',
  'A4264',
  'A4266',
  'A4281',
  'A4282',
  'A4283',
  'A4284',
  'A4285',
  'A4286',
  'A4670',
  'E0603',
  'G0008',
  'G0009',
  'G0010',
  'G0101',
  'G0104',
  'G0105',
  'G0106',
  'G0108',
  'G0109',
  'G0120',
  'G0121',
  'G0122',
  'G0123',
  'G0124',
  'G0130',
  'G0141',
  'G0143',
  'G0144',
  'G0145',
  'G0147',
  'G0148',
  'G0151',
  'G0157',
  'G0159',
  'G0270',
  'G0271',
  'G0296',
  'G0297',
  'G0328',
  'G0389',
  'G0396',
  'G0397',
  'G0402',
  'G0432',
  'G0433',
  'G0435',
  'G0438',
  'G0439',
  'G0442',
  'G0443',
  'G0444',
  'G0445',
  'G0446',
  'G0447',
  // 'G0463', removed per RVT-4598
  'G0472',
  'G0473',
  'G0475',
  'G0476',
  'G0485',
  'G0499',
  'G0500',
  'G6022',
  'G6024',
  'J1050',
  'J7296',
  'J7297',
  'J7298',
  'J7300',
  'J7301',
  'J7302',
  'J7306',
  'J7307',
  'P3000',
  'P3001',
  'Q0091',
  'Q2034',
  'Q2035',
  'Q2036',
  'Q2037',
  'Q2038',
  'Q2039',
  'S0265',
  'S0285',
  'S0446',
  'S0610',
  'S0612',
  'S0613',
  'S3620',
  'S3645',
  'S3850',
  'S4981',
  'S4989',
  'S8032',
  'S9131',
  'S9140',
  'S9141',
  'S9443',
  'S9452',
  'S9455',
  'S9460',
  'S9465',
  'S9470',
  'S9476',
  'U0003',
  'U0004',
  'U0005',
  'V5008',
];

const FULLY_COVERED_CODE_MODIFIER_COMBINATIONS: {
  code: string;
  modifier: string;
}[] = [
  {
    code: '99202',
    modifier: 'cs',
  },
  {
    code: '99203',
    modifier: 'cs',
  },
  {
    code: '99204',
    modifier: 'cs',
  },
  {
    code: '99205',
    modifier: 'cs',
  },
  {
    code: '99213',
    modifier: 'cs',
  },
  {
    code: '99214',
    modifier: 'cs',
  },
  {
    code: '99215',
    modifier: 'cs',
  },
];

export interface EstimationPdfSettings {
  disclaimer: string;
  includeSignature: boolean;
  includeServiceLocationContact: boolean;
  note: string | null;
  brandId?: number | null;
  noteTemplateId?: number | null;
  diagnosis: string | null;
  includeDiagnosis: boolean;
  includeAddress: boolean;
  shortFormEstimateEnabled?: boolean;
  includeGrossCharges?: boolean;
}

export type TreatmentType =
  | 'professional'
  | 'asc'
  | 'inpatientHospital'
  | 'outpatientHospital';

export type TreatmentLabel =
  | 'Professional'
  | 'Facility (ASC)'
  | 'Inpatient Hospital'
  | 'Outpatient Hospital';

export function treatmentLabel(t: TreatmentType): TreatmentLabel {
  switch (t) {
    case 'professional':
      return 'Professional';
    case 'asc':
      return 'Facility (ASC)';
    case 'inpatientHospital':
      return 'Inpatient Hospital';
    case 'outpatientHospital':
      return 'Outpatient Hospital';
  }
}

export function toBusinessUnitType(t: TreatmentType): BusinessUnitType {
  return t == 'outpatientHospital'
    ? 'hospital'
    : t == 'asc'
      ? 'asc'
      : 'professional';
}

export function toTreatmentType(t: BusinessUnitType) {
  return t == 'hospital'
    ? 'outpatientHospital'
    : t == 'asc'
      ? 'asc'
      : 'professional';
}

export interface InpatientPCSProcedure {
  codeToCrosswalk: string;
  pcsCode: string | null;
}
export interface EstimationTreatment {
  locationId: number | null;
  payers: EstimationPayer[];
  procedures: EstimationProcedure[];
  providerId: number | null;
  serviceDate: string;
  serviceTypes: ServiceTypeCode[];
  placeOfServiceCode: PlaceOfServiceCode | null;
  autoSelectServiceTypes: boolean;
  autoSelectExceptionApplied: boolean;
  autoSelectDetails: any[] | null;
  facilityType: BusinessUnitType | null;
  discount: EstimateDiscount | null;
  type: TreatmentType;
  inpatientTreatment: InpatientTreatment | null;
  diagnoses: string[];
  revenueCode: string | null;
}

export interface EstimateDiscount {
  type: 'percent' | 'amount';
  value: number;
}

export type EstimateResultAmount = {
  discountedTotal: number;
  deductible: number;
  coinsurance: number;
  copay: number;
};

export type InpatientTreatment = {
  diagnoses: string[];
  inpatientProcedures: InpatientPCSProcedure[];
  drgCode: string;
  estimatedStay: number;

  allowed: DrgAllowed | null;
};

export const DEFAULT_INPATIENT_TREATMENT: InpatientTreatment = {
  diagnoses: [],
  inpatientProcedures: [],
  drgCode: '',
  estimatedStay: 5,
  allowed: null,
};

/**
 * Estimation status
 */
export enum EstimationStatus {
  /** Draft */
  DRAFT = 'draft',
  /** Finalized */
  FINALIZED = 'finalized',
}

export enum TableEstimationStatus {
  /** Draft */
  DRAFT = 'draft',
  /** Finalized */
  FINALIZED = 'finalized',
  /** Opened by patient - SMS/email */
  OPENED = 'opened',
  /** Partial payment */
  PARTIAL = 'partial',
  /** Full payment */
  FULL = 'full',
}

export enum EstimationSentStatus {
  EMAIL = 'email',
  SMS = 'sms',
  PDF = 'pdf',
}

export enum SyncStatus {
  SUCCESS = 'success',
  PENDING = 'pending',
  FAILED = 'failure',
}

export namespace EstimationStatus {
  export function label(
    status: TableEstimationStatus | null,
    sentStatus: EstimationSentStatus[],
  ) {
    switch (status) {
      case TableEstimationStatus.FINALIZED:
        return sentStatus.length > 0
          ? ('Sent' as const)
          : ('Complete' as const);
      case TableEstimationStatus.OPENED:
        return 'Opened' as const;
      case TableEstimationStatus.PARTIAL:
        return 'Paid (partial)' as const;
      case TableEstimationStatus.FULL:
        return 'Paid (full)' as const;
      case TableEstimationStatus.DRAFT:
      case null:
        return 'Incomplete' as const;
    }
  }
}

/**
 * Payment request option type
 */
export enum EstimatePaymentRequestOptionType {
  /** Flat amount */
  FLAT = 'flat',
  /** Percentage */
  PERCENT = 'percent',
}

export interface EstimatePaymentRequestOption {
  type: EstimatePaymentRequestOptionType;
  value: number;
}
export interface EstimatePaymentRequest {
  enabled: boolean;
  dueDate: string | null;
  brandId: number | null;
  options: EstimatePaymentRequestOption[];
}

// This is a small piece of the config to make things work for the moment. It's an object so we can easily add properties to it.
export interface DbEstimationConfig {
  balanceBills: BalanceBills;
  autoSelectSTC: boolean;
}

export enum EditActionVisibility {
  SHOW,
  REMOVE,
}

export interface EstimateButtonVisibility {
  autosave: EditActionVisibility;
  estimateStatus: EditActionVisibility;
  assignee: EditActionVisibility;
  dueDate: EditActionVisibility;
  duplicate: EditActionVisibility;
  discard: EditActionVisibility;
  saveTemplate: EditActionVisibility;
  finalize: EditActionVisibility;
}

export const EstimateButtonVisibilityDefaults: EstimateButtonVisibility = {
  autosave: EditActionVisibility.REMOVE,
  estimateStatus: EditActionVisibility.REMOVE,
  assignee: EditActionVisibility.SHOW,
  dueDate: EditActionVisibility.SHOW,
  duplicate: EditActionVisibility.SHOW,
  discard: EditActionVisibility.SHOW,
  saveTemplate: EditActionVisibility.SHOW,
  finalize: EditActionVisibility.REMOVE,
};

/**
 * Estimation
 */
export interface Estimation {
  /** Estimate summary */
  estimate: Estimate | null;
  /** External ID */
  ref: string | null;
  /** Payment request */
  paymentRequest: EstimatePaymentRequest;
  /** PDF settings */
  pdfSettings: EstimationPdfSettings;
  /** Treatments */
  treatments: EstimationTreatment[];
  /** Config */
  config?: DbEstimationConfig;
  accuracyInfo?: EstimationClaimMultiMatch;
  /** Last transaction date */
  lastTransactionDate?: string | null;
}

export interface EstimationWorkflow {
  dueDate: string | null;
  assignedUserIds: number[];
}

export interface EstimationAutomationSettings {
  enabled: boolean;
  enabledAppointmentTypes: string[];
  enabledPayerIds: number[];
  daysBeforeAppointment: number;
  daysBeforeEnabled: boolean;
  onApptUpsertEnabled: boolean;
  defaultServiceLocationId: number | null;
  defaultServiceLocationEnabled: boolean;
  multiPayerEnabled: boolean;
  defaultProviderId: number | null;
  defaultProviderEnabled: boolean;
}

export interface EstimationAutomationError {
  configError: string;
  count: number;
}

export interface GetEstimationPropertiesGetForViewProperties {
  getEstimationPropertiesGetForViewProperties: GetEstimationPropertiesGet;
}
export interface GetEstimationPropertiesGet {
  getEstimationProperties: GetEstimationProperties;
}
export interface GetEstimationProperties {
  estimationProperties: EstimationProperties;
}

export interface EstimationProperties {
  getEstimation: GetEstimation;
}

export function defaultPhiRequestText(
  service: 'email' | 'sms',
  accountName: string,
): string {
  const smsStopTrailer = service === 'sms' ? ' Text STOP to unsubscribe.' : '';
  return `Hello from ${accountName}! This communication includes healthcare information related to the cost of care for your upcoming visit with our practice.${smsStopTrailer}`;
}

export function defaultEstimateMessageText(brandName: string): string {
  return `Your estimate from ${brandName} is ready. Click the link to view: Text STOP to unsubscribe.`;
}

export const spanishFooterText: string = `Traducción al español disponible. Haga clic en enlace para obtener más información.`;

export const USStateAbbreviations: { name: string; abbreviation: string }[] = [
  { abbreviation: 'AL', name: 'Alabama' },
  { abbreviation: 'AK', name: 'Alaska' },
  { abbreviation: 'AS', name: 'American Samoa' },
  { abbreviation: 'AZ', name: 'Arizona' },
  { abbreviation: 'AR', name: 'Arkansas' },
  { abbreviation: 'CA', name: 'California' },
  { abbreviation: 'CO', name: 'Colorado' },
  { abbreviation: 'CT', name: 'Connecticut' },
  { abbreviation: 'DE', name: 'Delaware' },
  { abbreviation: 'DC', name: 'District of Columbia' },
  { abbreviation: 'FM', name: 'Federated States of Micronesia' },
  { abbreviation: 'FL', name: 'Florida' },
  { abbreviation: 'GA', name: 'Georgia' },
  { abbreviation: 'GU', name: 'Guam' },
  { abbreviation: 'HI', name: 'Hawaii' },
  { abbreviation: 'ID', name: 'Idaho' },
  { abbreviation: 'IL', name: 'Illinois' },
  { abbreviation: 'IN', name: 'Indiana' },
  { abbreviation: 'IA', name: 'Iowa' },
  { abbreviation: 'KS', name: 'Kansas' },
  { abbreviation: 'KY', name: 'Kentucky' },
  { abbreviation: 'LA', name: 'Louisiana' },
  { abbreviation: 'ME', name: 'Maine' },
  { abbreviation: 'MH', name: 'Marshall Islands' },
  { abbreviation: 'MD', name: 'Maryland' },
  { abbreviation: 'MA', name: 'Massachusetts' },
  { abbreviation: 'MI', name: 'Michigan' },
  { abbreviation: 'MN', name: 'Minnesota' },
  { abbreviation: 'MS', name: 'Mississippi' },
  { abbreviation: 'MO', name: 'Missouri' },
  { abbreviation: 'MT', name: 'Montana' },
  { abbreviation: 'NE', name: 'Nebraska' },
  { abbreviation: 'NV', name: 'Nevada' },
  { abbreviation: 'NH', name: 'New Hampshire' },
  { abbreviation: 'NJ', name: 'New Jersey' },
  { abbreviation: 'NM', name: 'New Mexico' },
  { abbreviation: 'NY', name: 'New York' },
  { abbreviation: 'NC', name: 'North Carolina' },
  { abbreviation: 'ND', name: 'North Dakota' },
  { abbreviation: 'MP', name: 'Northern Mariana Islands' },
  { abbreviation: 'OH', name: 'Ohio' },
  { abbreviation: 'OK', name: 'Oklahoma' },
  { abbreviation: 'OR', name: 'Oregon' },
  { abbreviation: 'PW', name: 'Palau' },
  { abbreviation: 'PA', name: 'Pennsylvania' },
  { abbreviation: 'PR', name: 'Puerto Rico' },
  { abbreviation: 'RI', name: 'Rhode Island' },
  { abbreviation: 'SC', name: 'South Carolina' },
  { abbreviation: 'SD', name: 'South Dakota' },
  { abbreviation: 'TN', name: 'Tennessee' },
  { abbreviation: 'TX', name: 'Texas' },
  { abbreviation: 'UT', name: 'Utah' },
  { abbreviation: 'VT', name: 'Vermont' },
  { abbreviation: 'VI', name: 'Virgin Islands' },
  { abbreviation: 'VA', name: 'Virginia' },
  { abbreviation: 'WA', name: 'Washington' },
  { abbreviation: 'WV', name: 'West Virginia' },
  { abbreviation: 'WI', name: 'Wisconsin' },
  { abbreviation: 'WY', name: 'Wyoming' },
];

export const USStates = USStateAbbreviations.map(({ name }) => name);
/**
 * Currently, insurance_policy is not necessarily permanent.
 * This identifies an insurance policy by the payer ID and the order it
 * occurs in (in case of multiple polices by same payer).
 *
 * Eventually, this ought to be replaced with a stable integer
 * ID from the backend.
 */
export interface PatientPayerId {
  payerId: number | null;
  ordinal: number;
}

export namespace PatientPayerId {
  export function equal(): Equal<PatientPayerId> {
    return Equal.object({
      payerId: Equal.identity(),
      ordinal: Equal.identity(),
    });
  }

  export const KEY: KeyFn<PatientPayerId> = value =>
    JSON.stringify({ payerId: value.payerId, ordinal: value.ordinal });
}

export function patientPayerIds(payerIds: (number | null)[]): PatientPayerId[] {
  const counts = new Map<number | null, number>();
  return payerIds.map(payerId => {
    const count = counts.get(payerId) || 0;
    counts.set(payerId, count + 1);
    return {
      payerId,
      ordinal: count,
    };
  });
}

export function allowedToBig(allowed: AllowedEstimate) {
  return {
    allowed: allowed.allowed != null ? Number(allowed.allowed) : null,
    allowedType: allowed.allowedType,
    claimRate: allowed.claimRate != null ? Number(allowed.claimRate) : null,
    log: allowed.logs,
    grossCharge: allowed.grossCharge,
  };
}

export class EstimationProcedureFunctions {
  constructor(
    private readonly chargemasterItems: ChargemasterItem[],
    private readonly cptCodes: Cpt[],
  ) {
    this.cptOptions = this.cptCodes.map(cpt => cptToOption(cpt));
  }
  private readonly cptOptions: Option[] = [];

  getChargemasterItem(
    code: string,
    modifier: string,
  ): ChargemasterItem | undefined {
    for (const item of this.chargemasterItems) {
      if (
        item.code.toUpperCase() === code.toUpperCase() &&
        (item.modifier || '').toUpperCase() === modifier.toUpperCase()
      ) {
        return { ...item, modifier: item.modifier || '' };
      }
    }
  }

  getCpt(code: string): Cpt | undefined {
    for (const cpt of this.cptCodes) {
      if (code.toUpperCase() === cpt.code.toUpperCase()) {
        return cpt;
      }
    }
  }

  chargemasterProcedureCoverage(
    code: string,
    modifier: string,
  ): ProcedureCoverage | null {
    const chargemasterItem = this.getChargemasterItem(code, modifier);
    return chargemasterItem && chargemasterItem.selfPay
      ? ProcedureCoverage.NOT_COVERED
      : null;
  }

  private readonly notCoveredModifiers = ['ga', 'gx', 'ey', 'kb'];
  private readonly fullyCoveredModifiers = ['gl', 'gz'];

  modifierCoverage(modifier: string): ProcedureCoverage | null {
    return this.notCoveredModifiers.includes(modifier.toLowerCase())
      ? ProcedureCoverage.NOT_COVERED
      : this.fullyCoveredModifiers.includes(modifier.toLowerCase())
        ? ProcedureCoverage.FULLY_COVERED
        : null;
  }

  coverageExceptions(
    code: string,
    modifier: string,
    payers: EstimationPayer[],
    selfPayPayers: number[],
  ): ProcedureCoverage[] {
    const chargeMasterCoverage = this.chargemasterProcedureCoverage(
      code,
      modifier,
    );
    const modifierCoverage = this.modifierCoverage(modifier);
    return payers.map(payer => {
      const payerIsSelfPay: boolean =
        !!payer.payerId && selfPayPayers.includes(payer.payerId);
      if (
        payer.network == 'in' &&
        (PREVENTIVE_CARE_CODES.includes(code) ||
          FULLY_COVERED_CODE_MODIFIER_COMBINATIONS.findIndex(
            combination =>
              combination.code == code &&
              combination.modifier == modifier.toLowerCase(),
          ) != -1)
      ) {
        return payerIsSelfPay
          ? ProcedureCoverage.STANDARD
          : ProcedureCoverage.FULLY_COVERED;
      }
      const combinedCoverageVal: ProcedureCoverage =
        modifierCoverage || chargeMasterCoverage || ProcedureCoverage.STANDARD;
      if (
        combinedCoverageVal == ProcedureCoverage.FULLY_COVERED &&
        payerIsSelfPay
      ) {
        return ProcedureCoverage.STANDARD;
      }
      return combinedCoverageVal;
    });
  }

  allCptsAsOptions(): Option[] {
    return this.cptOptions;
  }
}

// This generates the default disclaimer text on an estimate.
export function estimateDefaultDisclaimerText(
  lang: Language,
  phone: string | null,
): string {
  const genericDisclaimer = getMessage('estimates.genericDisclaimer', lang);
  if (!phone) {
    return genericDisclaimer;
  }
  const phoneDisclaimer = getMessage(
    'estimates.genericDisclaimerPhone',
    lang,
    phone,
  );
  return `${genericDisclaimer} ${phoneDisclaimer}`;
}

export const estimateLinkSecret: string = '**RIVET-ESTIMATELINK**';

export interface SavedMedicarePartABenefits {
  deductiblePeriodDaysUsed: number;
  deductiblePeriodCostPerPeriod: number;
  deductiblePeriodTotalAmount: number;
  coinsurancePeriodDaysUsed: number;
  coinsurancePeriodDaysRemaining: number;
  coinsurancePeriodCostPerDay: number;
  coinsurancePeriodTotalAmount: number;
  reservePeriodDaysUsed: number;
  reservePeriodDaysRemaining: number;
  reservePeriodCostPerDay: number;
  reservePeriodTotalAmount: number;
}

export const emptySavedMedicarePartABenefits: SavedMedicarePartABenefits = {
  deductiblePeriodDaysUsed: 0,
  deductiblePeriodCostPerPeriod: 0,
  deductiblePeriodTotalAmount: 0,
  coinsurancePeriodDaysUsed: 0,
  coinsurancePeriodDaysRemaining: 0,
  coinsurancePeriodCostPerDay: 0,
  coinsurancePeriodTotalAmount: 0,
  reservePeriodDaysUsed: 0,
  reservePeriodDaysRemaining: 0,
  reservePeriodCostPerDay: 0,
  reservePeriodTotalAmount: 0,
};

export function generatePartABenefitsFromEligibility(
  eligibility: Partial<GetEligibility> | null | undefined,
  estimatedStay: number,
): SavedMedicarePartABenefits {
  if (
    !eligibility ||
    !eligibility?.response?.interpreted?.data.coverage ||
    !eligibility?.response?.interpreted.data.coverage.medicareRollingCoverage
  ) {
    return {
      ...emptySavedMedicarePartABenefits,
    };
  }
  const rollingCoverage: MedicareRollingCoverage =
    eligibility.response.interpreted.data.coverage.medicareRollingCoverage;

  const newPartA: SavedMedicarePartABenefits = {
    ...emptySavedMedicarePartABenefits,
  };
  // Fill out the costs per period/day from the values on the eligibility.
  newPartA.deductiblePeriodCostPerPeriod =
    rollingCoverage.partADeductible != null
      ? rollingCoverage.partADeductible
      : 0;

  newPartA.coinsurancePeriodCostPerDay =
    rollingCoverage.sixtyOneToNinetyDays.perDiemCost != null
      ? rollingCoverage.sixtyOneToNinetyDays.perDiemCost
      : 0;

  newPartA.reservePeriodCostPerDay =
    rollingCoverage.lifetimeReserve.perDiemCost != null
      ? rollingCoverage.lifetimeReserve.perDiemCost
      : 0;

  // Estimated stay is allowed to use half-days, but Medicare only cares about full days, so we truncate.
  let estimatedStayDaysLeft: number = Math.trunc(estimatedStay);
  // We will subtract from estimatedStayDaysLeft for the length of each period type until we hit 0.

  const daysRemainingInDeductiblePeriod: number =
    rollingCoverage.zeroToSixyDays.remainingDays != null
      ? rollingCoverage.zeroToSixyDays.remainingDays
      : 0;

  const daysRemainingInCoinsurancePeriod: number =
    rollingCoverage.sixtyOneToNinetyDays.remainingDays != null
      ? rollingCoverage.sixtyOneToNinetyDays.remainingDays
      : 0;

  const lifetimeReserveDaysRemaining: number =
    rollingCoverage.lifetimeReserve.remainingDays != null
      ? rollingCoverage.lifetimeReserve.remainingDays
      : 0;

  // Deductible Period
  if (estimatedStayDaysLeft > 0) {
    newPartA.deductiblePeriodDaysUsed = Math.min(
      estimatedStayDaysLeft,
      daysRemainingInDeductiblePeriod,
    );

    estimatedStayDaysLeft =
      estimatedStayDaysLeft - newPartA.deductiblePeriodDaysUsed;

    // The patient only owes the Medicare A Deductible if they still have all their deductible period days remaining.
    if (daysRemainingInDeductiblePeriod == 60) {
      newPartA.deductiblePeriodTotalAmount =
        newPartA.deductiblePeriodCostPerPeriod;
    } else {
      newPartA.deductiblePeriodTotalAmount = 0;
    }
  }

  // Coinsurance Period
  if (estimatedStayDaysLeft > 0) {
    newPartA.coinsurancePeriodDaysUsed = Math.min(
      estimatedStayDaysLeft,
      daysRemainingInCoinsurancePeriod,
    );

    estimatedStayDaysLeft =
      estimatedStayDaysLeft - newPartA.coinsurancePeriodDaysUsed;

    // The patient owes the "coinsurance" for each day of the coinsurance period used this treatment.
    newPartA.coinsurancePeriodTotalAmount =
      newPartA.coinsurancePeriodCostPerDay * newPartA.coinsurancePeriodDaysUsed;
  }
  newPartA.coinsurancePeriodDaysRemaining =
    daysRemainingInCoinsurancePeriod - newPartA.coinsurancePeriodDaysUsed;

  // Lifetime Reserve Period
  if (estimatedStayDaysLeft > 0) {
    newPartA.reservePeriodDaysUsed = Math.min(
      estimatedStayDaysLeft,
      lifetimeReserveDaysRemaining,
    );

    estimatedStayDaysLeft =
      estimatedStayDaysLeft - newPartA.reservePeriodDaysUsed;

    // The patient owes the "reserve coinsurance" for each day of the lifetime reserve period used this treatment.
    newPartA.reservePeriodTotalAmount =
      newPartA.reservePeriodCostPerDay * newPartA.reservePeriodDaysUsed;
  }
  newPartA.reservePeriodDaysRemaining =
    lifetimeReserveDaysRemaining - newPartA.reservePeriodDaysUsed;

  return newPartA;
}

export function partABenefitsMatch(
  first: SavedMedicarePartABenefits,
  second: SavedMedicarePartABenefits,
) {
  return (
    first.deductiblePeriodDaysUsed === second.deductiblePeriodDaysUsed &&
    first.deductiblePeriodCostPerPeriod ===
      second.deductiblePeriodCostPerPeriod &&
    first.deductiblePeriodTotalAmount === second.deductiblePeriodTotalAmount &&
    first.coinsurancePeriodDaysUsed === second.coinsurancePeriodDaysUsed &&
    first.coinsurancePeriodDaysRemaining ===
      second.coinsurancePeriodDaysRemaining &&
    first.coinsurancePeriodCostPerDay === second.coinsurancePeriodCostPerDay &&
    first.coinsurancePeriodTotalAmount ===
      second.coinsurancePeriodTotalAmount &&
    first.reservePeriodDaysUsed === second.reservePeriodDaysUsed &&
    first.reservePeriodDaysRemaining === second.reservePeriodDaysRemaining &&
    first.reservePeriodCostPerDay === second.reservePeriodCostPerDay &&
    first.reservePeriodTotalAmount === second.reservePeriodTotalAmount
  );
}

export interface EstimationClaimMultiMatch {
  estimationId: string | number;
  claimId: string | number;
  matchInfo: any;
  matchScore: number;
  problems?: string[];
}

export interface EstimationTotals {
  deductible: number;
  coinsurance: number;
  copay: number;
}

export interface EstimationClaimAccuracy {
  estimationClaimMultiMatchId: string | number;
  claimChargeId: string | number;
  estimationProcedureId: string | number;
  serviceDate: string;
  unit: number;
  code: string;
  modifier: string;
  allowed: number;
  patientResponsibility: number;
  source: 'claimCharge' | 'estimationProcedure' | 'unknown';
  coinsurance: number;
  copay: number;
  deductible: number;
  sequestration: number;
  accuracyProblemIds?: string[];
  matchProblemIds?: string[];
  id: string | number;
}

export interface AccuracyNote {
  note?: string;
}

// Auto Estimate Information
export enum AutoEstimateEventType {
  CREATE = 'create',
}

export interface AppointmentAutoEstimateEvent {
  id: number;
  created: string;
  estimationId: number | null;
  error: string[];
}
export interface AutoEstimatePatientHistory {
  id: number;
  lastActivity: string;
  sourceType: string;
}
export interface AppointmentAutoEstimateHistory {
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  externalRef: string;
  patientId: number;
  patientName: string;
  autoEstimateEvents: AppointmentAutoEstimateEvent[];
  lastPatientUpdate: AutoEstimatePatientHistory | null;
  whyNoRun: WhyAutoEstimateNoRun | null;
}
export interface WhyAutoEstimateNoRun {
  automationEnabled: boolean;
  daysBeforeEnabled: boolean;
  onApptUpsertEnabled: boolean;
  daysBeforeAppointment: number;
  appointmentTypeEnabled: boolean;
  payerEnabled: boolean;
  payerName: string | null;
  autoRunDate: string | null;
}

export type CCIEdit = {
  type: 'CCIEdit';
  payableCode: string;
  nonPayableCode: string;
  rationale: string;
  cciSet: 'hospital' | 'practitioner';
  modifier: '0' | '1' | '9';
};

export type MUEEdit = {
  type: 'MUEEdit';
  code: string;
  mueValue: number;
  mueSet: 'dme' | 'facility-outpatient' | 'practitioner';
  rationale: string;
  mueAdjudication: string;
};

export type PtOtEdit = {
  type: 'PtOtEdit';
  max: number;
  codes: string[];
};

export type ProcedureEdit = CCIEdit | MUEEdit | PtOtEdit;

export interface StcException {
  id: number;
  accountId: number;
  payerIds: string[];
  placeOfServiceCodes: PlaceOfServiceCode[];
  treatmentTypes: TreatmentType[];
  codes: string[];
  serviceType: string;
  insuranceProductIds: string[];
}

export function isUniqueStcException(
  stcException: StcException,
  existingExceptions: StcException[],
): boolean {
  return !existingExceptions.some(e => stcExceptionsOverlap(e, stcException));
}

export function stcExceptionsOverlap(
  stcException1: StcException,
  stcException2: StcException,
): boolean {
  return (
    stcException1.accountId === stcException2.accountId &&
    stcException1.payerIds.some(p => stcException2.payerIds.includes(p)) &&
    stcException1.codes.some(c => stcException2.codes.includes(c)) &&
    stcException1.treatmentTypes.some(t =>
      stcException2.treatmentTypes.includes(t),
    ) &&
    (stcException1.insuranceProductIds.length === 0 ||
      stcException2.insuranceProductIds.length === 0 ||
      stcException1.insuranceProductIds.some(ip =>
        stcException2.insuranceProductIds.includes(ip),
      )) &&
    stcException1.id !== stcException2.id
  );
}

export type SuggestedLineItem = {
  code: string;
  revCode: string | null;
  modifier: string;
  quantity: string;
  charged: string;
};

export type SuggestedClaim = {
  claim_payer_info_id: string;
  transaction_date: string;
  total_charge: string;
  services: SuggestedLineItem[];
};

export type SuggestedLineItems = {
  suggestions: SuggestedClaim[];
};

export type SuggestedLineItemId = SuggestedLineItem & Id;

export type EstimationAnalyticsBenefits = {
  hasAlternateCoinsurance: boolean;
  hasAlternateCopay: boolean;
  hasAlternateDeductible: boolean;
  hasAlternateOOP: boolean;
  hasAlternateLimitation: boolean;
  hasCustomCoinsurance: boolean;
  hasCustomCopay: boolean;
  hasCustomDeductible: boolean;
  hasCustomOOP: boolean;
};

export interface DbEstimation {
  /** Estimation */
  estimation: Estimation;
  /** Patient ID */
  patientId: number;
  /** Status */
  status: EstimationStatus;
  /** Status for <estimation-status> */
  estimationStatus?: TableEstimationStatus;
  /** Most recent event for <estimation-status> */
  mostRecentEvent?: { event: string; timestamp: string } | null;

  accuracyInfo?: EstimationClaimMultiMatch;
}

export interface DbEstimationUser {
  type: 'user' | 'api-user' | 'automated';
  id: number | null;
}

export type BenefitDetailsModalParams = {
  benefitType: BenefitItemType;
  payerIndex: number;
  treatmentIndex: number;
};

export function estimationFilename(
  userType: 'patient' | 'user',
  input: {
    name?: { first?: string | null; last?: string | null };
    procedureDate?: Date | null;
    filenameProperties?: string[] | null;
    ehrId?: string | null;
    appointmentTime?: string | null;
  },
): string {
  const now = LocalDateTime.now();
  const dateTimeFormatter = DateTimeFormatter.ofPattern('MMddyyyy_HHmm');
  const dateFormatter = DateTimeFormatter.ofPattern('MMddyyyy');

  const data = {
    estimateLiteral: 'Estimate',
    currentDateTime: dateTimeFormatter.format(now),
    currentDate: dateFormatter.format(now),
    procedureDate: input.procedureDate ? isoDate(input.procedureDate) : null,
    ehrId: input?.ehrId,
    appointmentTime: input.appointmentTime,
    fullName:
      input.name?.last && input.name?.first
        ? `${input.name.last}, ${input.name.first}`
        : (input.name?.last ?? input.name?.first ?? null),
  };

  if (userType === 'patient') {
    return `${joinProperties(
      data,
      ['estimateLiteral', 'procedureDate'],
      '-',
    )}.pdf`;
  }

  const defaultProperties = ['fullName', 'currentDateTime'];
  const props =
    (input.filenameProperties ?? []).length > 0
      ? input.filenameProperties!
      : defaultProperties;
  return `${joinProperties(data, props)}.pdf`;
}

export function joinProperties(
  data: Record<string, string | null | undefined>,
  properties: string[],
  delimiter = '_',
): string {
  return properties
    .map(s => data[s])
    .filter((e): e is string => e != null)
    .join(delimiter);
}
